import { useCallback, useEffect, useState } from "react";
import { HttpError, request } from "../../../api/http";
import { useConfirm } from "../../../components/Dialog";
import {
  BrokerEmployee,
  BrokerEmployeesResponse,
  EMPLOYEE_STATUS_LABELS,
  EMPLOYEE_STATUS_STYLES,
  EmployeeStatus,
} from "../../../types/brokers";

const fmtDateTime = (iso: string | null): string => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const fmtDate = (iso: string | null): string => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

export default function BrokerEmployeesTab({
  brokerInternalId,
  token,
}: {
  brokerInternalId: number;
  token: string | null;
}) {
  const confirm = useConfirm();
  const [items, setItems] = useState<BrokerEmployee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await request<BrokerEmployeesResponse>(
        `/api/admin/operations/brokers/${brokerInternalId}/employees`,
        { token },
      );
      setItems(response.items);
    } catch (err) {
      setError(
        err instanceof HttpError ? `HTTP ${err.status}` : "No se pudo cargar",
      );
    } finally {
      setLoading(false);
    }
  }, [brokerInternalId, token]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (employee: BrokerEmployee, target: "enabled" | "disabled") => {
    const action = target === "enabled" ? "Activar" : "Deshabilitar";
    const tone = target === "enabled" ? "primary" : "danger";
    const fullName =
      [employee.name, employee.lastname].filter(Boolean).join(" ") || employee.email;
    const ok = await confirm({
      title: `${action} empleado`,
      message: `El empleado "${fullName}" pasará a estado ${EMPLOYEE_STATUS_LABELS[target]}. ¿Continuar?`,
      confirmLabel: action,
      tone,
    });
    if (!ok) return;
    setBusyId(employee.id);
    setError(null);
    try {
      await request(
        `/api/admin/operations/brokers/${brokerInternalId}/employees/${employee.id}/status`,
        { method: "PATCH", body: { status: target }, token },
      );
      await load();
    } catch (err) {
      if (err instanceof HttpError) {
        const body = err.body as { error?: string } | undefined;
        if (body?.error === "CANNOT_DISABLE_OWNER") {
          setError(
            "No se puede deshabilitar al owner. Transfiere la propiedad primero.",
          );
        } else {
          setError(`HTTP ${err.status}`);
        }
      } else {
        setError("No se pudo actualizar");
      }
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return <div style={{ color: "#73879C" }}>Cargando empleados…</div>;
  }

  return (
    <div>
      {error && (
        <div
          style={{
            background: "#fde2e2",
            color: "#b32424",
            padding: "0.5rem 0.7rem",
            borderRadius: "0.4rem",
            marginBottom: "0.8rem",
            fontSize: ".82rem",
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.6rem",
          marginBottom: "0.6rem",
          fontSize: ".82rem",
          color: "#73879C",
        }}
      >
        <strong style={{ color: "#1f2933" }}>{items.length}</strong> empleados
      </div>

      <div
        style={{
          background: "white",
          borderRadius: "0.5rem",
          border: "1px solid #e9ecef",
          overflow: "auto",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: ".82rem",
          }}
        >
          <thead>
            <tr style={{ background: "#f8f9fb" }}>
              <Th>#</Th>
              <Th>Empleado</Th>
              <Th>Email</Th>
              <Th>Teléfono</Th>
              <Th>Tipo</Th>
              <Th>Estado</Th>
              <Th>Creado</Th>
              <Th>Último acceso</Th>
              <Th>Roles</Th>
              <Th>Acciones</Th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td
                  colSpan={10}
                  style={{
                    padding: "1.2rem",
                    textAlign: "center",
                    color: "#73879C",
                  }}
                >
                  Sin empleados.
                </td>
              </tr>
            )}
            {items.map((employee) => {
              const validStatus = employee.status as EmployeeStatus;
              const style =
                EMPLOYEE_STATUS_STYLES[validStatus] ??
                EMPLOYEE_STATUS_STYLES.disabled;
              const statusLabel =
                EMPLOYEE_STATUS_LABELS[validStatus] ?? employee.status;
              return (
                <tr
                  key={employee.id}
                  style={{
                    borderTop: "1px solid #f1f3f5",
                    verticalAlign: "top",
                  }}
                >
                  <Td>
                    <span style={{ color: "#73879C", fontFamily: "monospace" }}>
                      {employee.id}
                    </span>
                  </Td>
                  <Td>
                    {[employee.name, employee.lastname]
                      .filter(Boolean)
                      .join(" ") || (
                      <span style={{ color: "#adb5bd" }}>—</span>
                    )}
                  </Td>
                  <Td>
                    <a
                      href={`mailto:${employee.email}`}
                      style={{ color: "#0069d9", textDecoration: "none" }}
                    >
                      {employee.email}
                    </a>
                  </Td>
                  <Td>
                    {employee.phone ? (
                      <a
                        href={`tel:${employee.phone}`}
                        style={{ color: "#0069d9", textDecoration: "none" }}
                      >
                        {employee.phone}
                      </a>
                    ) : (
                      <span style={{ color: "#adb5bd" }}>—</span>
                    )}
                  </Td>
                  <Td>
                    <span
                      style={{
                        fontSize: ".7rem",
                        fontWeight: 600,
                        background:
                          employee.type === "owner" ? "#cfe2ff" : "#e9ecef",
                        color:
                          employee.type === "owner" ? "#084298" : "#495057",
                        padding: "0.1rem 0.45rem",
                        borderRadius: "0.35rem",
                      }}
                    >
                      {employee.type}
                    </span>
                  </Td>
                  <Td>
                    <span
                      style={{
                        display: "inline-block",
                        padding: "0.15rem 0.45rem",
                        borderRadius: "0.35rem",
                        fontSize: ".68rem",
                        fontWeight: 600,
                        background: style.bg,
                        color: style.fg,
                        border: `1px solid ${style.border}`,
                      }}
                    >
                      {statusLabel}
                    </span>
                  </Td>
                  <Td>{fmtDate(employee.createdAt)}</Td>
                  <Td>{fmtDateTime(employee.lastSeenAt)}</Td>
                  <Td>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.15rem",
                      }}
                    >
                      {employee.roles.length === 0 ? (
                        <span style={{ color: "#adb5bd" }}>—</span>
                      ) : (
                        employee.roles.map((r) => (
                          <span
                            key={r}
                            style={{
                              fontFamily: "monospace",
                              fontSize: ".68rem",
                              color: "#495057",
                            }}
                          >
                            {r.replace(/^ROLE_/, "")}
                          </span>
                        ))
                      )}
                    </div>
                  </Td>
                  <Td>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.25rem",
                        minWidth: 110,
                      }}
                    >
                      {employee.status !== "enabled" && (
                        <ActionButton
                          tone="primary"
                          onClick={() => updateStatus(employee, "enabled")}
                          disabled={busyId !== null}
                          loading={busyId === employee.id}
                        >
                          Activar
                        </ActionButton>
                      )}
                      {employee.status !== "disabled" &&
                        employee.type !== "owner" && (
                          <ActionButton
                            tone="danger"
                            onClick={() => updateStatus(employee, "disabled")}
                            disabled={busyId !== null}
                            loading={busyId === employee.id}
                          >
                            Deshabilitar
                          </ActionButton>
                        )}
                    </div>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        padding: "0.55rem 0.65rem",
        textAlign: "left",
        fontWeight: 600,
        color: "#1f2933",
        fontSize: ".68rem",
        textTransform: "uppercase",
        letterSpacing: ".3px",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return (
    <td style={{ padding: "0.5rem 0.65rem" }}>{children}</td>
  );
}

function ActionButton({
  onClick,
  tone,
  loading,
  disabled,
  children,
}: {
  onClick: () => void;
  tone: "primary" | "danger";
  loading?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  const palette =
    tone === "danger"
      ? { bg: "#fde2e2", fg: "#b32424", border: "#f5c6cb" }
      : { bg: "#0069d9", fg: "white", border: "#0069d9" };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "0.25rem 0.55rem",
        borderRadius: "0.35rem",
        background: palette.bg,
        color: palette.fg,
        border: `1px solid ${palette.border}`,
        fontSize: ".72rem",
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled && !loading ? 0.6 : 1,
      }}
    >
      {loading ? "…" : children}
    </button>
  );
}
